import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { getList } from "../common/BackendProvider";

const formatPrice = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue.toFixed(2) : "-";
};

const formatScore = (value) => {
  const numericValue = Number(value || 0);
  return `${numericValue > 0 ? "+" : ""}${numericValue.toFixed(0)}`;
};

const scoreTone = (value) => {
  const numericValue = Number(value || 0);
  if (numericValue > 5) return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (numericValue > 2) return "border-lime-200 bg-lime-50 text-lime-700";
  if (numericValue > -1) return "border-amber-200 bg-amber-50 text-amber-700";
  if (numericValue > -4) return "border-orange-200 bg-orange-50 text-orange-700";
  return "border-red-200 bg-red-50 text-red-700";
};

const getLogoUrl = (company) => {
  if (company?.logo_url) return company.logo_url;
  if (!company?.website) return null;
  const website = company.website.startsWith("http") ? company.website : `https://${company.website}`;
  try {
    return `https://www.google.com/s2/favicons?domain=${new URL(website).hostname}&sz=64`;
  } catch (e) {
    return null;
  }
};

const CompanyLogo = ({ company }) => {
  const [failed, setFailed] = useState(false);
  const logoUrl = getLogoUrl(company);

  return (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      {logoUrl && !failed ? (
        <img src={logoUrl} alt="" className="h-full w-full object-contain p-1" onError={() => setFailed(true)} />
      ) : (
        <span className="text-sm font-bold text-emerald-600">{(company?.symbol || "?").slice(0, 2)}</span>
      )}
    </div>
  );
};

const CollapsibleSection = ({ title, eyebrow, summary, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
        aria-expanded={open}
      >
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-600">{eyebrow}</div>
          <h2 className="mt-1 text-xl font-bold text-slate-950">{title}</h2>
          <p className="mt-1 text-sm font-semibold text-slate-500">{summary}</p>
        </div>
        <span className="rounded-md border border-slate-200 px-3 py-1 text-sm font-bold text-slate-600">
          {open ? "Hide" : "Show"}
        </span>
      </button>
      {open ? <div className="border-t border-slate-100 p-5">{children}</div> : null}
    </section>
  );
};

const MetricTile = ({ label, value, detail }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
    <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">{label}</div>
    <div className="mt-2 text-2xl font-bold text-slate-950">{value}</div>
    <div className="mt-1 text-sm font-semibold text-slate-500">{detail}</div>
  </div>
);

const ActivityItem = ({ company, type = "Viewed" }) => (
  <Link to={`/company/${company.symbol}`} className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 transition hover:border-emerald-200 hover:bg-emerald-50">
    <div className="flex min-w-0 items-center gap-3">
      <CompanyLogo company={company} />
      <div className="min-w-0">
        <div className="truncate text-sm font-bold text-slate-950">{company.name}</div>
        <div className="text-xs font-semibold text-slate-500">{type} company signal</div>
      </div>
    </div>
    <div className="shrink-0 text-sm font-bold text-slate-500">{company.symbol}</div>
  </Link>
);

const PortfolioRow = ({ company }) => {
  const low = Number(company.low_24h || 0);
  const high = Number(company.high_24h || 0);
  const last = Number(company.last_price || 0);
  const range = high > low ? Math.max(0, Math.min(100, ((last - low) / (high - low)) * 100)) : 50;

  return (
    <Link to={`/company/${company.symbol}`} className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition hover:border-emerald-200 hover:shadow-md md:grid-cols-[minmax(0,1fr)_130px_180px] md:items-center">
      <div className="flex min-w-0 items-center gap-3">
        <CompanyLogo company={company} />
        <div className="min-w-0">
          <div className="truncate text-sm font-bold text-slate-950">{company.name}</div>
          <div className="text-xs font-semibold text-slate-500">{company.symbol}</div>
        </div>
      </div>
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">Last price</div>
        <div className="mt-1 text-lg font-bold text-slate-950">{formatPrice(company.last_price)}</div>
      </div>
      <div>
        <div className="mb-2 flex justify-between text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">
          <span>Day range</span>
          <span>{formatPrice(low)} - {formatPrice(high)}</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-emerald-500" style={{ width: `${range}%` }} />
        </div>
      </div>
    </Link>
  );
};

const PerformerCard = ({ company }) => (
  <Link to={`/company/${company.symbol}`} className="group block rounded-lg border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-md">
    <div className="flex items-start justify-between gap-4">
      <div className="flex min-w-0 items-start gap-3">
        <CompanyLogo company={company} />
        <div className="min-w-0">
          <div className="truncate text-lg font-bold text-slate-950 group-hover:text-emerald-700">{company.name}</div>
          <div className="mt-1 text-sm font-semibold text-slate-500">{company.symbol}</div>
        </div>
      </div>
      <div className={`shrink-0 rounded-full border px-3 py-1 text-sm font-bold ${scoreTone(company.total_score)}`}>
        {formatScore(company.total_score)}
      </div>
    </div>
    <div className="mt-5 grid gap-3 sm:grid-cols-3">
      <MetricTile label="Last Price" value={formatPrice(company.last_price)} detail="market" />
      <MetricTile label="ESG" value={formatScore(company.esg_score)} detail="score" />
      <MetricTile label="Financial" value={formatScore(company.financial_score)} detail="score" />
    </div>
  </Link>
);

const EmptyState = ({ title, detail }) => (
  <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-8 text-center">
    <div className="text-base font-bold text-slate-950">{title}</div>
    <div className="mt-2 text-sm font-semibold text-slate-500">{detail}</div>
  </div>
);

export const Dashboard = () => {
  const [bestPerformers, setBestPerformers] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [recentCompanies, setRecentCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedRecent = JSON.parse(localStorage.getItem("recentCompanies") || "[]");
    setRecentCompanies(storedRecent);

    Promise.all([
      getList("companies/best-performers", {
        filter: { hasprice: 0 },
        pagination: { page: 1, perPage: 6 },
        sort: { field: "financial_score", order: "desc" },
      }),
      getList("watchlist", { pagination: { page: 1, perPage: 100 } }),
    ]).then(([performersResponse, watchlistResponse]) => {
      setBestPerformers(performersResponse.data.companies || []);
      setPortfolio(watchlistResponse.data.companies || []);
      setLoading(false);
    }).catch(() => {
      toast.error("Error while loading dashboard data", {});
      setLoading(false);
    });
  }, []);

  const portfolioValue = useMemo(() => {
    return portfolio.reduce((sum, company) => sum + Number(company.last_price || 0), 0);
  }, [portfolio]);

  return (
    <div className="space-y-5 p-4 lg:p-8">
      <ToastContainer />
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-600">Workspace overview</div>
        <h1 className="mt-2 text-3xl font-bold text-slate-950">Dashboard</h1>
        <p className="mt-2 max-w-3xl text-sm font-medium leading-6 text-slate-500">
          Monitor research activity, portfolio watch items, and system-identified company opportunities from one screen.
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <MetricTile label="Recent Activity" value={recentCompanies.length} detail="searched companies" />
        <MetricTile label="Portfolio Watch" value={portfolio.length} detail={`tracked stocks, ${formatPrice(portfolioValue)} total price`} />
        <MetricTile label="Best Performers" value={bestPerformers.length} detail="system ranked companies" />
      </div>

      <CollapsibleSection
        eyebrow="Activity"
        title="Recent Activity"
        summary="Companies you searched and future stock purchase events."
      >
        <div className="grid gap-3 xl:grid-cols-2">
          {recentCompanies.slice(0, 6).map((company) => (
            <ActivityItem key={`${company.symbol}-${company.searched_at || ""}`} company={company} />
          ))}
        </div>
        {recentCompanies.length === 0 ? (
          <EmptyState title="No recent company searches yet" detail="Use the top search bar to open a company and it will appear here." />
        ) : null}
        <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-800">
          Stock purchase activity will appear here once portfolio transaction capture is connected.
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        eyebrow="Portfolio"
        title="Portfolio"
        summary="Portfolio stocks and visible market performance signals."
      >
        <div className="grid gap-3">
          {portfolio.map((company) => (
            <PortfolioRow key={company.id || company.symbol} company={company} />
          ))}
        </div>
        {!loading && portfolio.length === 0 ? (
          <EmptyState title="No portfolio stocks yet" detail="Add companies to your Watch List from a company page to start tracking them here." />
        ) : null}
      </CollapsibleSection>

      <CollapsibleSection
        eyebrow="Signals"
        title="Best Performers"
        summary="Companies identified by the system using available financial, ESG, risk, and market data."
      >
        <div className="grid gap-4 xl:grid-cols-2">
          {bestPerformers.map((company) => (
            <PerformerCard key={company.id || company.symbol} company={company} />
          ))}
        </div>
        {!loading && bestPerformers.length === 0 ? (
          <EmptyState title="No performers available" detail="The system needs company market and score data before ranking best performers." />
        ) : null}
      </CollapsibleSection>
    </div>
  );
};
